import type { OriginUpdateType } from '@/types/origin'
import { mountingHolesText, refId, refLabel } from '@/lib/dd-queue/changeRequestDetails'
import { WHITE_INK_LABELS } from '@/lib/dd-queue/finishingFromSpec'
import { joinMulti } from '@/lib/dd-queue/attributeMulti'

/** '0.5"' — the Rounded Corners attribute wording (`${radius}"`), from a ref, a custom ref or a string. */
function roundedCornersAttributeText(value: unknown): string | null {
  if (value == null) return null
  if (typeof value === 'string') return value.trim() || null
  if (typeof value !== 'object') return null
  const ref = value as { id?: unknown; value?: unknown; label?: unknown }
  if (ref.id === 'custom') {
    return typeof ref.value === 'number' && Number.isFinite(ref.value) ? `${ref.value}"` : refLabel(ref)
  }
  const id = refId(ref)
  return id ? `${id}"` : refLabel(ref)
}

/** White-ink codes → the multi-value attribute text the picker stores ("Flood White; Spot White"). */
function whiteInkAttributeValue(value: unknown): string | null {
  if (!Array.isArray(value)) return null
  const labels = value
    .filter((c): c is string => typeof c === 'string' && c.trim() !== '')
    .map((c) => WHITE_INK_LABELS[c] ?? c)
  return labels.length ? joinMulti(labels) : null
}

// DD-TRIAGE-DEFAULTS-001 (F-083 Candace Loran): on a product update the Deliverables group starts
// with Pre-press and Web Image; Vendor File is offered ("add if the product already has it"), not
// pre-ticked.
const SPEC_STEPS = {
  always: ['design', 'estimation', 'production_file', 'web_image', 'product_review', 'pim_publish', 'piq_quickpart', 'web_publish'],
  conditional: ['outsource_file', 'signagent_svg', 'instructions', 'assembled_view', 'install_notes', 'hardware'],
}
const DESIGN_STEPS = {
  always: ['design', 'sales_approval', 'production_file', 'web_image', 'product_review', 'pim_publish', 'piq_quickpart', 'web_publish'],
  conditional: ['outsource_file', 'signagent_svg', 'instructions', 'assembled_view'],
}

export const MODIFY_DELIVERABLES: Record<
  OriginUpdateType,
  { always: string[]; conditional: string[] }
> = {
  price: { always: ['pim_publish', 'piq_quickpart', 'web_publish'], conditional: [] },
  // F-138 (Matthew, Justin — 2026-10-12): the UIN is printed on the sign, so a UIN add / change
  // goes through Design before Pre-press (no Web Image, no Sales Review — nobody asked for them).
  uin: {
    always: ['uin', 'design', 'production_file', 'outsource_file', 'pim_publish', 'piq_quickpart', 'web_publish'],
    conditional: [],
  },
  web: { always: ['web_publish'], conditional: [] },
  spec: SPEC_STEPS,
  design: DESIGN_STEPS,
  sb2: SPEC_STEPS,
  sb3: DESIGN_STEPS,
  sb4: SPEC_STEPS,
  sb5: SPEC_STEPS,
  sb6: { always: [], conditional: [] },
  // F-099 slice 7
  parts: SPEC_STEPS,
  artwork: DESIGN_STEPS,
  std_custom: {
    always: ['pim_publish', 'piq_quickpart', 'web_publish'],
    conditional: [],
  },
  discontinue: { always: ['web_publish', 'piq_quickpart', 'pim_publish'], conditional: [] },
  bundle_edit: {
    always: ['sku', 'uin', 'web_image', 'product_review', 'pim_publish', 'web_publish'],
    conditional: [],
  },
}

export function requiredStepsForChanges(types: OriginUpdateType[]): Set<string> {
  const out = new Set<string>()
  for (const t of types) {
    for (const k of MODIFY_DELIVERABLES[t]?.always ?? []) out.add(k)
  }
  return out
}

export function conditionalStepsForChanges(types: OriginUpdateType[]): Set<string> {
  const out = new Set<string>()
  for (const t of types) {
    for (const k of MODIFY_DELIVERABLES[t]?.conditional ?? []) out.add(k)
  }
  return out
}

/**
 * New attribute values implied by a change request — reference modify.ts L78–147.
 * Pre-fills PMA attributes and drives the "Modified" pill.
 */
export function changedAttributeValues(changes: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {}

  const price = changes.price as { new_price?: number } | undefined
  if (price?.new_price != null) out.price = `$${Number(price.new_price).toFixed(2)}`

  const uin = changes.uin as { uin_action?: string; new_uin?: string | null } | undefined
  if (uin) out.uin = uin.uin_action === 'remove' ? '(removed)' : (uin.new_uin ?? '')

  // DD-CHG-CARD-001 — the wire carries `{ id, label }` refs for catalog picks (see refLabel);
  // the attribute pickers store labels, mounting holes / rounded corners the packet wording.
  const spec = changes.spec as {
    new_substrate?: unknown
    new_width?: number | null
    new_height?: number | null
    single_double?: string | null
    new_finishing?: unknown
    taping?: string | null
    rounded_corners?: unknown
    mounting_holes?: unknown
    hardware?: string | boolean | null
    grommets?: string | null
    hem?: string | null
  } | undefined
  if (spec) {
    const substrate = refLabel(spec.new_substrate)
    if (substrate) out.base_substrate = substrate
    if (spec.new_width != null) out.width = `${spec.new_width}"`
    if (spec.new_height != null) out.height = `${spec.new_height}"`
    if (spec.single_double) {
      out.single_double =
        spec.single_double === 'double'
          ? 'Double-sided'
          : spec.single_double === 'single'
            ? 'Single-sided'
            : 'No Print'
    }
    const laminate = refLabel(spec.new_finishing)
    if (laminate) out.laminate = laminate
    if (spec.taping) out.taping = spec.taping
    const corners = roundedCornersAttributeText(spec.rounded_corners)
    if (corners) out.rounded_corners = corners
    const holes = mountingHolesText(spec.mounting_holes)
    if (holes) out.mounting_holes = holes
    if (spec.hardware != null && spec.hardware !== false) {
      out.hardware_needed = 'Yes'
      if (typeof spec.hardware === 'string' && spec.hardware.trim()) {
        out.hardware_description = spec.hardware.trim()
      }
    }
    if (spec.grommets) out.grommets = spec.grommets
    if (spec.hem) out.hem = spec.hem
  }

  const design = changes.design as {
    graphic_method?: unknown
    vinyl_type?: unknown
    rta_color?: string | null
    white_ink?: unknown
    reference_sku?: { sku?: string; label?: string } | null
  } | undefined
  if (design) {
    const graphicMethod = refLabel(design.graphic_method)
    if (graphicMethod) out.graphic_method = graphicMethod
    const vinylType = refLabel(design.vinyl_type)
    if (vinylType) out.vinyl_type = vinylType
    if (design.rta_color) out.rta_color = design.rta_color
    const whiteInk = whiteInkAttributeValue(design.white_ink)
    if (whiteInk) out.white_ink = whiteInk
    if (design.reference_sku?.sku) {
      out.reference_sku = design.reference_sku.label
        ? `${design.reference_sku.sku} — ${design.reference_sku.label}`
        : design.reference_sku.sku
    }
  }

  const stdCustom = changes.std_custom as { is_standard_custom?: boolean } | undefined
  if (stdCustom?.is_standard_custom) out.std_type = 'Standard-Custom'

  return out
}
